import { environment } from '@app-environments/environment';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { io } from 'socket.io-client';
import {Observable, Subject} from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class PtySocketService {
	private socket: any = null;
	private socketUrl: string = '';
	private isSocketIoConnectedPromise: Promise<boolean> = null;
	private waitForSocketIoToConnect: Subject<null> = null;
	private authToken: string = null;
	// Set when the server says the session expired, consumed by the 'disconnect' handler. See the
	// comment on the auth:expired handler for why the reconnect cannot happen inline.
	private reconnectAfterDisconnect = false;

	constructor(
		private router: Router,
	) {
		this.socketUrl = environment.socket_end_point;
		// Create a subject and a promise.  Then promise will let us be able to know if socketIO is connected or block for it
		// The subject will let us send a message to the promise that socketIo has finished connecting
		this.waitForSocketIoToConnect = new Subject();
		this.isSocketIoConnectedPromise = new Promise((resolve, reject) => {
			this.waitForSocketIoToConnect.subscribe({ next: () => resolve(true) });
		});
	}

	public connect(user: any, token: string) {
		this.authToken = token || null;
		this.socket = io(this.socketUrl, {
			/*
			 * The socket server authenticates the connection from this JWT. `auth` rather than
			 * `query` keeps the token out of the upgrade URL, and therefore out of load-balancer
			 * and proxy access logs. The callback form is re-evaluated on every reconnect attempt,
			 * so a refreshed token is picked up without rebuilding the socket.
			 */
			auth: (cb) => cb({ token: this.readFreshToken() }),
			query: { user: JSON.stringify(user) },
			transports: [ 'websocket' ],
		});
		this.socket.on('connect_error', (err: any) => {
			// socket.io retries forever by default. A rejected token will never start working, so
			// stop reconnecting instead of hammering the server until the session is renewed.
			if (err && typeof err.message === 'string' && err.message.startsWith('auth_')) {
				console.warn('Socket authentication failed, not retrying:', err.message);
				this.socket.disconnect();
			}
		});

		/*
		 * The server drops sockets whose token has expired, and socket.io does NOT auto-reconnect
		 * after a server-initiated disconnect -- it reports 'io server disconnect' and stops. So
		 * without this the CRM would silently lose live messaging at the token's exp and only
		 * recover on a full page reload.
		 *
		 * The reconnect has to happen from the 'disconnect' handler, NOT from here. The server emits
		 * auth:expired and disconnects back-to-back, so this handler runs while the socket is still
		 * connected -- and socket.io-client's connect() begins `if (this.connected) return`, making
		 * the call a silent no-op. Verified against socket.io-client 4.x: reconnecting here leaves
		 * the server seeing one connection, reconnecting on disconnect leaves it seeing two.
		 */
		this.socket.on('auth:expired', () => {
			console.warn('Socket session expired, will reconnect with a fresh token');
			this.reconnectAfterDisconnect = true;
		});

		this.socket.on('disconnect', () => {
			if (!this.reconnectAfterDisconnect) {
				return;
			}
			this.reconnectAfterDisconnect = false;
			// Now that the socket is actually closed this opens a new one, and the auth callback
			// above re-reads access_token -- so a refreshed session comes straight back.
			this.socket.connect();
		});

		/*
		 * Revoked means the session was ended by a logout, so there is no fresher token to pick up.
		 * Only mobile sessions are revocable today (path-crm-backend has no revocation), so this is
		 * defensive -- but the event is part of the protocol and ignoring it would be worse.
		 */
		/*
		 * The server denied a room join or a room-targeted send. Nothing user-facing yet -- the
		 * denial is by design and the affected feature is simply absent -- but without this the only
		 * trace is a server log, and the `unavailable` reason (a database blip during authorization)
		 * presents to the user as a message that silently never arrives. Logging it client-side
		 * turns a "chat is broken" report into something diagnosable from a console screenshot.
		 */
		this.socket.on('room:unauthorized', (denial: any) => {
			console.warn('Socket room access denied', denial);
		});

		this.socket.on('auth:revoked', () => {
			console.warn('Socket session revoked, returning to login');
			this.socket.disconnect();
			this.router.navigate([ '/login' ]);
		});

		this.waitForSocketIoToConnect.next();
	}

	private readFreshToken(): string {
		return localStorage.getItem('access_token') || this.authToken || '';
	}

	disconnect() {
		if (this.socket)
			this.socket.disconnect();
	}

	listen(eventName: string) {
		// I hate this code setup, but I'm forced into it because of this stupid rxjs Observable usage.
		// Basically, using a promise here to block until SocketIO is connected
		return new Observable( (Subscriber) => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on(eventName, (data) => {
					Subscriber.next(data);
				});
			});
		});
	}

	emit(eventName: string, data: any) {
		this.socket.emit(eventName, data);
	}

	sendMessage(message: any) {
		if (this.socket)
			this.socket.emit('message:send', message);
	}

	getMessage() {
		return this.socket.fromEvent('message:receive');
	}

	getMessages() {
		return new Observable(observer => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on('message:receive', (data: any) => {
					observer.next(data);
				});
			});
		});
	}

	subscribeToGroup(groupUUID: any) {
		if (this.socket)
			this.socket.emit('group:subscribe', { group_uuid: groupUUID });
	}

	unsubscribeFromGroup(groupUUID: any) {
		if (this.socket)
			this.socket.emit('group:unsubscribe', { group_uuid: groupUUID });
	}

	sendGroupMessage(message: any) {
		if (this.socket)
			this.socket.emit('group:message:send', message);
	}
	sendReassignContact(message: any) {
		if (this.socket)
			this.socket.emit('reassign:contact:send', message);
	}

	getGroupMessages() {
		return new Observable(observer => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on('group:message:receive', (data) => {
					observer.next(data);
				});
			});

			return () => {
				this.disconnect();
			};
		});
	}

	messagesTyping() {
		return new Observable(observer => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on('group:message:typing', (data) => {
					observer.next(data);
				});
			});

			return () => {
				this.disconnect();
			};
		});
	}

	subscribeToAdminContact(contactUUID: any) {
		if (this.socket) {
			this.socket.emit('adminConversation:subscribe', { admin_conversation_uuid: contactUUID });
		}
	}

	unsubscribeFromAdminContact(contactUUID: any) {
		if (this.socket) {
			this.socket.emit('adminConversation:unsubscribe', { admin_conversation_uuid: contactUUID });
		}
	}

	sendAdminMessage(message: any) {
		if (this.socket) {
			this.socket.emit('adminConversation:message:send', message);
		}
	}

	getAdminMessages() {
		return new Observable(observer => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on('adminConversation:message:receive', (data) => {
					observer.next(data);
				});
			});
			return () => {
				this.disconnect();
			};
		});
	}

	subscribeToAdminOmContact(contactUUID: any) {
		if (this.socket) {
			this.socket.emit('adminOmConversation:subscribe', { admin_conversation_uuid: contactUUID });
		}
	}

	unsubscribeFromAdminOmContact(contactUUID: any) {
		if (this.socket) {
			this.socket.emit('adminOmConversation:unsubscribe', { admin_conversation_uuid: contactUUID });
		}
	}

	sendAdminOmMessage(message: any) {
		if (this.socket) {
			this.socket.emit('adminOmConversation:message:send', message);
		}
	}

	getAdminOmMessages() {
		return new Observable(observer => {
			this.isSocketIoConnectedPromise.then(() => {
				this.socket.on('adminOmConversation:message:receive', (data) => {
					observer.next(data);
				});
			});
			return () => {
				this.disconnect();
			};
		});
	}

	// getGroupMessages() {
	//   let observable = new Observable(observer => {
	//     this.socket.on('group:message:receive', (data: any) => {
	//       observer.next(data);
	//     });
	//   })
	//   return observable;
	// }
}
